/*
 * Copyright (c) 2023 Trimble Inc. All rights reserved.
 */

import { CircularProgress, Paper, Theme, IconButton } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LegacyGrid from './legacy/LegacyGrid';
import { withStyles } from '@mui/styles';
import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { categories, Category, CategoryType } from '../common/Categories';
import { checkUserAuthorized } from '../common/common';
import { selectLicenseChartData, LICENSE_BAR_CHART_PAGE_SIZE, sliceBarChartPage } from '../common/licenseChartData';
import { loadFromLocalStorage } from '../common/localStorage';
import { actionCreators } from '../store/Dashboard';
import DataTable from './DataTable';
import GroupedColumnChart from './GroupedColumnChart';
import LicenseDoughnutChart from './LicenseDoughnutChart';
import MyTab from './MyTab';


interface Props {
    theme: Theme;
    classes?: any;
    requestChartData: Function;
    chartData: any;
    isLoading: boolean;
    error: any;
    history?: any;
    totalCount: any;
    isChartLoading: boolean;
}
const styles = (theme: Theme) => ({
    root: {
        flexGrow: 1,
        marginBottom: 24
    },
    progress: {
        margin: ((theme as any) && typeof (theme as any).spacing === 'function') ? (theme as any).spacing(2) : '16px'
    },
    columnChart: {
        width: 'auto'
    },
    doughnutChart: {
        width: 'auto',
        marginLeft: 24
    },
    chartPaper: {
        padding: '10px',
        margin: '10px'
    },
    message: {
        borderRadius: '0px',
        textAlign: 'center' as const,
        padding: '20px',
        marginTop: '20px'
    },
    progressWrap: {
        textAlign: 'center' as const,
        marginTop: '20vh'
    },
    grid: {
        justifyContent: 'space-between',
        alignItems: 'center'
    },
    datatable: {
        width: '100%',
        padding: '12px'
    },
    barChartPager: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: '4px',
        padding: '4px 8px 0',
        fontSize: '13px',
        color: '#666'
    }
});

class License extends React.Component<Props, {}> {
    state = {
        isUnauthorized: false,
        selectedCategoryId: 4,
        barChartMounted: false,
        barChartPage: 0
    };
    filters: any;
    private barChartScheduleId: number | null = null;
    private chartLoadStarted = false;

    componentDidMount() {
        const enabledModules: any = checkUserAuthorized();
        if (enabledModules == null) {
            return;
        }
        if (!this.chartLoadStarted) {
            this.chartLoadStarted = true;
            this.loadData();
        }
    }

    componentDidUpdate(prevProps: Props) {
        if (this.props.isLoading && !prevProps.isLoading) {
            this.setState({ barChartMounted: false, barChartPage: 0 });
        }

        const prevUserCount = prevProps.chartData?.xaxisData?.length ?? 0;
        const userCount = this.props.chartData?.xaxisData?.length ?? 0;
        if (userCount !== prevUserCount) {
            this.setState({ barChartPage: 0, barChartMounted: false });
        }

        const barReady =
            !this.props.isLoading &&
            !this.props.isChartLoading &&
            this.props.chartData?.datasets !== undefined &&
            this.props.chartData?.xaxisData !== undefined;

        if (barReady && !this.state.barChartMounted && this.barChartScheduleId === null) {
            const mountBarChart = () => {
                this.barChartScheduleId = null;
                this.setState({ barChartMounted: true });
            };
            if (typeof window.requestIdleCallback === 'function') {
                this.barChartScheduleId = window.requestIdleCallback(mountBarChart, { timeout: 1000 }) as unknown as number;
            } else {
                this.barChartScheduleId = window.requestAnimationFrame(mountBarChart);
            }
        }
    }

    componentWillUnmount() {
        if (this.barChartScheduleId !== null) {
            if (typeof window.cancelIdleCallback === 'function') {
                window.cancelIdleCallback(this.barChartScheduleId);
            } else {
                window.cancelAnimationFrame(this.barChartScheduleId);
            }
        }
    }

    loadData() {
        this.props.requestChartData(
            this.state.selectedCategoryId,
            new Date(),
            new Date()
        );
    }
    handleTabChange() {

    }
    refreshChart(filters: any) {
        this.filters = filters;
        this.props.requestChartData(
            this.state.selectedCategoryId,
            new Date(),
            new Date(),
            filters.length >= 1 ? { filters } : undefined
        );
    }

    changeBarChartPage(delta: number) {
        const totalUsers = this.props.chartData?.xaxisData?.length ?? 0;
        const maxPage = Math.max(0, Math.ceil(totalUsers / LICENSE_BAR_CHART_PAGE_SIZE) - 1);
        const nextPage = Math.min(maxPage, Math.max(0, this.state.barChartPage + delta));
        if (nextPage !== this.state.barChartPage) {
            this.setState({ barChartPage: nextPage, barChartMounted: false });
        }
    }

    render() {
        const { classes } = this.props;
        const selectedCategory: Category | undefined = categories.find(
            (category: Category) => {
                return category.id === this.state.selectedCategoryId;
            }
        );
        const isDisabled =
            selectedCategory !== undefined ? selectedCategory.isDateDisabled : false;
        let errorFetchingModules = false;
        const enabledModules: Array<any> = loadFromLocalStorage('Enabled Modules');
        let filteredCategories: Array<Category> = [];
        if (enabledModules != null && enabledModules.length !== 0) {
            if (enabledModules[0] !== -99) {
                filteredCategories = categories.filter((category: Category) => {
                    return enabledModules.find((module: any) => {
                        return module.id - 1 === category.id && category.id === 4;
                    }) !== undefined;
                });
            }
            else {
                errorFetchingModules = true;
            }
        }
        const userCount = this.props.chartData?.xaxisData?.length ?? 0;
        const barChartPageCount = Math.max(1, Math.ceil(userCount / LICENSE_BAR_CHART_PAGE_SIZE));
        const barChartRangeStart = userCount === 0 ? 0 : this.state.barChartPage * LICENSE_BAR_CHART_PAGE_SIZE + 1;
        const barChartRangeEnd = Math.min(userCount, (this.state.barChartPage + 1) * LICENSE_BAR_CHART_PAGE_SIZE);
        const pagedBarChart = this.props.chartData
            ? sliceBarChartPage(this.props.chartData, this.state.barChartPage)
            : undefined;

        return (
            (!this.state.isUnauthorized && !errorFetchingModules) ?
                (
                    <div className={classes.root}>
                        {/*Tab Control*/}
                        <LegacyGrid container>
                            <LegacyGrid item>
                                <MyTab
                                    categories={filteredCategories}
                                    selectedTab={this.state.selectedCategoryId}
                                    onTabChange={this.handleTabChange.bind(this)}
                                />
                            </LegacyGrid>
                        </LegacyGrid>
                        {/*Chart Control*/}
                        {this.props.isLoading ?
                            (
                                <div className={classes.progressWrap}>
                                    <CircularProgress className={classes.progress} color="secondary" />
                                </div>
                            ) :
                            this.props.chartData === undefined || this.props.error !== undefined ?
                                (
                                    <Paper className={classes.message}>
                                        Oops ! An unexpected error occurred.Please try again later
                                    </Paper>
                                ) : this.props.totalCount === 0 ?
                                    (
                                        <Paper className={classes.message}>No data available</Paper>
                                    ) :
                                    (
                                        <div>
                                            {(selectedCategory !== undefined ?
                                                (this.props.isChartLoading ||
                                                    this.props.chartData.groupedDoughnutData === undefined) ?
                                                    <div className={classes.progressWrap}>
                                                        <CircularProgress className={classes.progress} color="secondary" />
                                                    </div> :
                                                    (<LegacyGrid container className={classes.grid} style={{ flexWrap: 'nowrap' }}>
                                                        <LegacyGrid item xs={8} className={classes.columnChart}>
                                                            {this.state.barChartMounted &&
                                                                pagedBarChart?.datasets !== undefined &&
                                                                pagedBarChart?.xaxisData !== undefined ? (
                                                                <>
                                                                    <GroupedColumnChart
                                                                        key={`license-bar-${this.state.barChartPage}-${pagedBarChart.xaxisData.length}`}
                                                                        datasets={pagedBarChart.datasets}
                                                                        xaxisLabel={selectedCategory.xaxisLabel}
                                                                        xaxisData={pagedBarChart.xaxisData}
                                                                        yaxisLabel={selectedCategory.yaxisLabel}
                                                                        MaxBarNum={pagedBarChart.maxBarnum}
                                                                        label={selectedCategory.chartLabel}
                                                                        category={selectedCategory}
                                                                    />
                                                                    {userCount > LICENSE_BAR_CHART_PAGE_SIZE && (
                                                                        <div className={classes.barChartPager}>
                                                                            <IconButton
                                                                                size="small"
                                                                                disabled={this.state.barChartPage === 0}
                                                                                onClick={() => this.changeBarChartPage(-1)}
                                                                                aria-label="Previous users"
                                                                            >
                                                                                <ChevronLeftIcon />
                                                                            </IconButton>
                                                                            <span>
                                                                                {barChartRangeStart}–{barChartRangeEnd} of {userCount}
                                                                            </span>
                                                                            <IconButton
                                                                                size="small"
                                                                                disabled={this.state.barChartPage >= barChartPageCount - 1}
                                                                                onClick={() => this.changeBarChartPage(1)}
                                                                                aria-label="Next users"
                                                                            >
                                                                                <ChevronRightIcon />
                                                                            </IconButton>
                                                                        </div>
                                                                    )}
                                                                </>
                                                            ) : (
                                                                <div className={classes.progressWrap}>
                                                                    <CircularProgress className={classes.progress} color="secondary" />
                                                                </div>
                                                            )}
                                                        </LegacyGrid>
                                                        <LegacyGrid item xs={4} className={classes.doughnutChart}>
                                                            <LicenseDoughnutChart
                                                                labels={this.props.chartData.groupedDoughnutLabel}
                                                                dataSets={this.props.chartData.groupedDoughnutData}
                                                                licenseTypeTexts={this.props.chartData.licenseTypeTexts}
                                                            />
                                                        </LegacyGrid>
                                                    </LegacyGrid>) :
                                                <div />)}
                                        </div>
                                    )}
                        {/*Table Control — show as soon as chart summary arrives; do not wait for bar chart render*/}
                        {!this.props.isLoading && this.props.error === undefined &&
                            <LegacyGrid container>
                                <LegacyGrid item className={classes.datatable}>
                                    <DataTable
                                        startDate={new Date()}
                                        endDate={new Date()}
                                        selectedCategory={selectedCategory}
                                        refreshChart={this.refreshChart.bind(this)}
                                    />
                                </LegacyGrid>
                            </LegacyGrid>}

                    </div>) :

                <Paper className={classes.message}>
                    {errorFetchingModules ? 'Unable to fetch data.Please try again later' : 'You are not authorized to view the data.'}
                </Paper>

        );
    }
}

const mapStateToProps = (state: any) => ({
    state: state.dashboard,
    isLoading: state.dashboard.isLoading,
    chartData: selectLicenseChartData(state),
    totalCount: state.dashboard.totalCount,
    error: state.dashboard.error,
    isChartLoading: state.dashboard.isChartLoading
});

const mapDispatchtoProps = (dispatch: any) =>
    bindActionCreators(actionCreators, dispatch);
export default connect(
    mapStateToProps,
    mapDispatchtoProps
)(withStyles(styles, { withTheme: true })(License));
